import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getAmbassadorDetails } from "../../../../apis/ambassadors.api";
import { getClientDetails } from "../../../../apis/clients.api";
import { isSelectedTimeSlotPast } from "../../../../services/date-and-time.utils";
import { convertToFullName } from "../../../../services/utils";
import {
  canShowSendMeetingReminder,
  getTimelineState,
} from "../../../invitations/components/project-status-timeline/utils";
import type { DashboardCalendarEventTooltipProps } from "./types";

function useCalendarEventTooltip({
  event,
  isOpen,
  onOpen,
  onClose,
}: Pick<DashboardCalendarEventTooltipProps, "event" | "isOpen" | "onOpen" | "onClose">) {
  const {
    ambassadorId,
    clientId,
    zoomURL,
    isAmbassadorAttending,
    isAmbassadorAttend,
  } = event;
  const isAmbassadorConfirmed =
    isAmbassadorAttending ?? isAmbassadorAttend ?? false;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [ambassador, setAmbassador]: any = useState();
  const [client, setClient]: any = useState();
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpen === false) {
      setAnchorEl(null);
    }
  }, [isOpen]);

  const fetchInvitationUsers = async () => {
    if (!clientId || !ambassadorId) return;

    setIsLoadingUsers(true);
    try {
      const [clientDetails, ambassadorDetails]: any = await Promise.all([
        dispatch(getClientDetails(clientId)),
        dispatch(getAmbassadorDetails(ambassadorId)),
      ]);

      setAmbassador(ambassadorDetails?.data);
      setClient(clientDetails?.data);
    } catch (error) {
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const open = Boolean(anchorEl) && (isOpen ?? true);
  const showSendReminder = canShowSendMeetingReminder(event);
  const status = event.invitationStatus ?? event.status;
  const { isClosedStatus, isPastSuccess } = getTimelineState(status);
  const isScheduledTimeExpired =
    isSelectedTimeSlotPast(event.selectedTimeSlot) &&
    !isPastSuccess &&
    !isClosedStatus;
  const hasActions = Boolean(
    zoomURL || showSendReminder || isScheduledTimeExpired
  );
  const invitationRecordId = event.iId || event.id;
  const ambassadorName = convertToFullName(
    ambassador?.firstName,
    ambassador?.lastName
  );
  const clientName = convertToFullName(client?.firstName, client?.lastName);

  useEffect(() => {
    if (open) {
      setHintOpen(false);
    }
  }, [open]);

  const handleClose = () => {
    setAnchorEl(null);
    onClose?.();
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setHintOpen(false);

    if (open) {
      handleClose();
      return;
    }

    setAnchorEl(e.currentTarget);
    fetchInvitationUsers();
    onOpen?.();
  };

  const handleAddMemberClick = () => {
    handleClose();
    setShowAddMemberDialog(true);
  };

  return {
    ambassador,
    client,
    anchorEl,
    open,
    hintOpen,
    setHintOpen,
    showAddMemberDialog,
    setShowAddMemberDialog,
    isLoadingUsers,
    isAmbassadorConfirmed,
    showSendReminder,
    isScheduledTimeExpired,
    hasActions,
    invitationRecordId,
    ambassadorName,
    clientName,
    zoomURL,
    handleClose,
    handleOpen,
    handleAddMemberClick,
  };
}

export { useCalendarEventTooltip };
